import { useState } from "react";
import axios from "axios";
import { motion } from "framer-motion";
import { Check } from "lucide-react";
import { toast } from "sonner";
import { EASE } from "@/components/motion/Reveal";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const AUDIENCE_TYPES = [
  "Corporate",
  "Professional Association",
  "Women's Leadership",
  "University/College",
  "Youth/Student",
  "School",
  "Nonprofit",
  "Other",
];

const FORMATS = ["Keynote", "Workshop", "Panel", "Other"];
const DELIVERY = ["In-Person", "Virtual"];

const ChipGroup = ({ label, options, value, onChange, testid }) => (
  <div>
    <p className="font-mono text-[10px] tracking-[0.25em] uppercase text-white/40 mb-4">{label}</p>
    <div className="flex flex-wrap gap-2" data-testid={testid}>
      {options.map((o) => (
        <button
          key={o}
          type="button"
          data-testid={`${testid}-${o.toLowerCase().replace(/[\s/']+/g, "-")}`}
          onClick={() => onChange(o)}
          className={`px-4 py-2 font-mono text-[10px] tracking-[0.15em] uppercase border transition-colors duration-300 ${
            value === o
              ? "bg-gold text-obsidian border-gold"
              : "border-white/20 text-white/60 hover:border-gold/60 hover:text-gold"
          }`}
        >
          {o}
        </button>
      ))}
    </div>
  </div>
);

export default function SpeakingInquiryForm() {
  const empty = {
    name: "", email: "", phone: "", organization: "",
    event_name: "", event_date: "", event_location: "",
    audience_type: "Corporate", audience_size: "", requested_topic: "",
    format: "Keynote", delivery: "In-Person", event_budget: "", additional_details: "",
  };
  const [form, setForm] = useState(empty);
  const [status, setStatus] = useState("idle");

  const inputCls = "w-full bg-transparent border-b border-white/15 focus:border-gold outline-none py-3 text-ivory placeholder:text-white/25 transition-colors duration-300";
  const labelCls = "font-mono text-[10px] tracking-[0.25em] uppercase text-white/40";
  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const submit = async (e) => {
    e.preventDefault();
    setStatus("sending");
    try {
      await axios.post(`${API}/speaking-inquiries`, form);
      setStatus("sent");
    } catch {
      setStatus("idle");
      toast.error("Something went wrong. Please try again.");
    }
  };

  if (status === "sent") {
    return (
      <motion.div
        data-testid="speaking-inquiry-success"
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.6, ease: EASE }}
        className="border border-gold/40 p-10 text-center"
      >
        <span className="inline-flex items-center justify-center w-12 h-12 rounded-full border border-gold text-gold mb-6">
          <Check size={20} />
        </span>
        <p className="font-serif text-2xl text-ivory mb-3">Thank you, {form.name.split(" ")[0]}.</p>
        <p className="text-sm font-light text-white/50">
          Your speaking inquiry has been received. Sharon's team will respond within 1 to 2 business days.
        </p>
        <button
          data-testid="speaking-inquiry-send-another-button"
          onClick={() => { setStatus("idle"); setForm(empty); }}
          className="mt-8 font-mono text-[11px] tracking-[0.25em] uppercase text-gold hover:text-gold-dark transition-colors"
        >
          Submit Another Inquiry
        </button>
      </motion.div>
    );
  }

  return (
    <form onSubmit={submit} data-testid="speaking-inquiry-form" className="space-y-8">
      <div className="grid sm:grid-cols-2 gap-8">
        <div>
          <label className={labelCls} htmlFor="spk-name">Name *</label>
          <input id="spk-name" data-testid="speaking-inquiry-name-input" required value={form.name} onChange={set("name")} placeholder="Your name" className={inputCls} />
        </div>
        <div>
          <label className={labelCls} htmlFor="spk-email">Email *</label>
          <input id="spk-email" data-testid="speaking-inquiry-email-input" required type="email" value={form.email} onChange={set("email")} placeholder="you@company.com" className={inputCls} />
        </div>
      </div>

      <div className="grid sm:grid-cols-2 gap-8">
        <div>
          <label className={labelCls} htmlFor="spk-phone">Phone</label>
          <input id="spk-phone" data-testid="speaking-inquiry-phone-input" type="tel" value={form.phone} onChange={set("phone")} placeholder="(555) 000-0000" className={inputCls} />
        </div>
        <div>
          <label className={labelCls} htmlFor="spk-organization">Organization</label>
          <input id="spk-organization" data-testid="speaking-inquiry-organization-input" value={form.organization} onChange={set("organization")} placeholder="Company, school, or organization" className={inputCls} />
        </div>
      </div>

      <div className="grid sm:grid-cols-2 gap-8">
        <div>
          <label className={labelCls} htmlFor="spk-event-name">Event Name</label>
          <input id="spk-event-name" data-testid="speaking-inquiry-event-name-input" value={form.event_name} onChange={set("event_name")} placeholder="Annual Leadership Summit" className={inputCls} />
        </div>
        <div>
          <label className={labelCls} htmlFor="spk-event-date">Event Date</label>
          <input id="spk-event-date" data-testid="speaking-inquiry-event-date-input" type="date" value={form.event_date} onChange={set("event_date")} className={`${inputCls} [color-scheme:dark]`} />
        </div>
      </div>

      <div className="grid sm:grid-cols-2 gap-8">
        <div>
          <label className={labelCls} htmlFor="spk-event-location">Event Location</label>
          <input id="spk-event-location" data-testid="speaking-inquiry-event-location-input" value={form.event_location} onChange={set("event_location")} placeholder="City, venue, or virtual" className={inputCls} />
        </div>
        <div>
          <label className={labelCls} htmlFor="spk-audience-size">Estimated Audience Size</label>
          <input id="spk-audience-size" data-testid="speaking-inquiry-audience-size-input" value={form.audience_size} onChange={set("audience_size")} placeholder="e.g. 150" className={inputCls} />
        </div>
      </div>

      <ChipGroup label="Audience Type" options={AUDIENCE_TYPES} value={form.audience_type} onChange={(v) => setForm({ ...form, audience_type: v })} testid="speaking-inquiry-audience-type" />

      <div>
        <label className={labelCls} htmlFor="spk-topic">Requested Topic</label>
        <input id="spk-topic" data-testid="speaking-inquiry-topic-input" value={form.requested_topic} onChange={set("requested_topic")} placeholder="e.g. The Beginning of What's Next" className={inputCls} />
      </div>

      <div className="grid sm:grid-cols-2 gap-8">
        <ChipGroup label="Format" options={FORMATS} value={form.format} onChange={(v) => setForm({ ...form, format: v })} testid="speaking-inquiry-format" />
        <ChipGroup label="Delivery" options={DELIVERY} value={form.delivery} onChange={(v) => setForm({ ...form, delivery: v })} testid="speaking-inquiry-delivery" />
      </div>

      <div>
        <label className={labelCls} htmlFor="spk-budget">Event Budget</label>
        <input id="spk-budget" data-testid="speaking-inquiry-budget-input" value={form.event_budget} onChange={set("event_budget")} placeholder="Optional" className={inputCls} />
      </div>

      <div>
        <label className={labelCls} htmlFor="spk-details">Additional Details</label>
        <textarea id="spk-details" data-testid="speaking-inquiry-details-input" rows={4} value={form.additional_details} onChange={set("additional_details")} placeholder="Tell us about your audience, your goals, and your event." className={`${inputCls} resize-none`} />
      </div>

      <button
        type="submit"
        data-testid="speaking-inquiry-submit-button"
        disabled={status === "sending"}
        className="bg-gold text-obsidian px-10 py-4 font-mono text-[11px] tracking-[0.25em] uppercase hover:bg-gold-dark transition-colors duration-300 disabled:opacity-50"
      >
        {status === "sending" ? "Sending…" : "Submit Speaking Inquiry"}
      </button>
    </form>
  );
}
